import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useChat } from '../contexts/ChatContext';
import api from '../lib/api';
import LinkifiedText from '../components/LinkifiedText';
import { 
  Send, Hash, User, Users, Loader2, CheckSquare, Smile, ChevronLeft, ChevronDown, MessageSquare, ArrowLeft, Trash2, Lock, Globe
} from 'lucide-react';
import { toast } from 'sonner';
import { io } from 'socket.io-client';

const EMOJIS = ['👍', '❤️', '😊', '🎉', '👀', '🔥', '✅', '💪'];

const Chat = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const workspaceId = searchParams.get('workspace');
  const conversationIdParam = searchParams.get('conversation');
  const projectIdParam = searchParams.get('project');
  
  const { user } = useAuth();
  const { currentWorkspace, workspaces } = useWorkspace();
  const { t } = useLanguage();
  const { markConversationRead } = useChat();
  
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const [targetUserId, setTargetUserId] = useState('');
  const [chatMembers, setChatMembers] = useState([]);
  
  const messagesEndRef = useRef(null);
  const socketRef = useRef(null);
  const inputRef = useRef(null);

  const selectedWorkspace = workspaces.find(w => w.workspace_id === workspaceId) || currentWorkspace;

  // Handle workspace change
  const handleWorkspaceChange = (wsId) => {
    setSearchParams({ workspace: wsId });
    setActiveConversation(null);
    setMessages([]);
    setMobileShowChat(false);
  };

  // Handle conversation selection
  const handleSelectConversation = (conv) => {
    setActiveConversation(conv);
    setMobileShowChat(true);
  };

  // Handle back to conversation list on mobile
  const handleBackToList = () => {
    setMobileShowChat(false);
  };

  // Fetch workspace members for targeted messaging
  useEffect(() => {
    if (!selectedWorkspace?.workspace_id) { setChatMembers([]); return; }
    const fetchMembers = async () => {
      try {
        const res = await api.get(`/workspaces/${selectedWorkspace.workspace_id}`);
        setChatMembers((res.data.members || []).filter(m => m.user_id !== user?.user_id));
      } catch { setChatMembers([]); }
    };
    fetchMembers();
  }, [selectedWorkspace?.workspace_id, user?.user_id]);

  const fetchConversations = useCallback(async () => {
    if (!selectedWorkspace) return;
    
    try {
      const response = await api.get('/conversations', {
        params: { workspace_id: selectedWorkspace.workspace_id }
      });
      setConversations(response.data.conversations || []);
      
      // Auto-select conversation from URL deep link or first conversation
      const convList = response.data.conversations || [];
      if (convList.length > 0 && !activeConversation) {
        let targetConv = null;
        if (conversationIdParam) {
          targetConv = convList.find(c => c.conversation_id === conversationIdParam);
        }
        if (!targetConv && projectIdParam) {
          targetConv = convList.find(c => c.project_id === projectIdParam);
        }
        setActiveConversation(targetConv || convList[0]);
      }
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedWorkspace, activeConversation, conversationIdParam, projectIdParam]);

  const fetchMessages = useCallback(async () => {
    if (!activeConversation) return;
    
    try {
      const response = await api.get(`/conversations/${activeConversation.conversation_id}/messages`);
      setMessages(response.data.messages || []);
      
      // Mark as read and reset unread count locally + globally via context
      try {
        await markConversationRead(activeConversation.conversation_id);
        // Update local unread count to 0
        setConversations(prev => prev.map(conv => 
          conv.conversation_id === activeConversation.conversation_id 
            ? { ...conv, unread_count: 0 }
            : conv
        ));
      } catch (e) {
        // Silent fail for mark-read
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  }, [activeConversation, markConversationRead]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    if (activeConversation) {
      fetchMessages();
    }
  }, [activeConversation, fetchMessages]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Socket.IO connection for real-time updates
  useEffect(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const socket = io(backendUrl, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      timeout: 10000
    });
    
    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('Socket connected');
      socket.emit('authenticate', {
        user_id: user?.user_id,
        workspace_id: selectedWorkspace?.workspace_id
      });
    });

    socket.on('connect_error', (error) => {
      console.log('Socket connection error, using polling fallback:', error.message);
    });

    socket.on('new_message', (message) => {
      // Update messages if we're in the same conversation
      if (message.conversation_id === activeConversation?.conversation_id) {
        setMessages(prev => {
          // Avoid duplicates
          if (prev.some(m => m.message_id === message.message_id)) {
            return prev;
          }
          return [...prev, message];
        });
      } else {
        // Update unread count for other conversations
        setConversations(prev => prev.map(conv => {
          if (conv.conversation_id === message.conversation_id && message.sender_id !== user?.user_id) {
            return { ...conv, unread_count: (conv.unread_count || 0) + 1 };
          }
          return conv;
        }));
      }
    });

    socket.on('unread_update', (data) => {
      setConversations(prev => prev.map(conv => {
        if (conv.conversation_id === data.conversation_id) {
          return { ...conv, unread_count: data.unread_count };
        }
        return conv;
      }));
    });

    socket.on('message_reaction', (data) => {
      if (data.conversation_id === activeConversation?.conversation_id) {
        setMessages(prev => prev.map(msg => 
          msg.message_id === data.message_id 
            ? { ...msg, reactions: data.reactions }
            : msg
        ));
      }
    });

    socket.on('notification', (data) => {
      if (data.type === 'new_message') {
        // Update unread for this conversation
        setConversations(prev => prev.map(conv => {
          if (conv.conversation_id === data.conversation_id) {
            return { ...conv, unread_count: (conv.unread_count || 0) + 1 };
          }
          return conv;
        }));
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [user, selectedWorkspace, activeConversation]);

  // Polling fallback for real-time updates (every 5 seconds when active)
  useEffect(() => {
    if (!activeConversation) return;

    const pollMessages = async () => {
      try {
        const response = await api.get(`/conversations/${activeConversation.conversation_id}/messages`);
        const newMessages = response.data.messages || [];
        setMessages(prev => {
          // Only update if there are new messages
          if (newMessages.length > prev.length) {
            return newMessages;
          }
          return prev;
        });
      } catch (error) {
        // Silent fail for polling
      }
    };

    const interval = setInterval(pollMessages, 5000);
    return () => clearInterval(interval);
  }, [activeConversation]);

  // Join conversation room when active conversation changes
  useEffect(() => {
    if (socketRef.current && activeConversation) {
      socketRef.current.emit('join_conversation', {
        conversation_id: activeConversation.conversation_id
      });
    }
  }, [activeConversation]);

  const sendMessage = async () => {
    if (!newMessage.trim() || !activeConversation) return;

    setSending(true);
    const messageContent = newMessage;
    const targetId = (targetUserId && targetUserId !== 'everyone') ? targetUserId : null;
    setNewMessage('');
    
    try {
      const response = await api.post('/messages', {
        content: messageContent,
        conversation_id: activeConversation.conversation_id,
        target_user_id: targetId
      });
      
      setMessages(prev => {
        if (prev.some(m => m.message_id === response.data.message_id)) {
          return prev;
        }
        return [...prev, response.data];
      });
      setTargetUserId('');
    } catch (error) {
      toast.error('Failed to send message');
      setNewMessage(messageContent);
    } finally {
      setSending(false);
    }
  };

  const handleReaction = async (messageId, emoji) => {
    // Optimistic update
    setMessages(prev => prev.map(msg => {
      if (msg.message_id === messageId) {
        const existingReactions = msg.reactions || [];
        const userReactionIndex = existingReactions.findIndex(
          r => r.user_id === user?.user_id && r.emoji === emoji
        );
        
        let newReactions;
        if (userReactionIndex >= 0) {
          // Remove reaction
          newReactions = existingReactions.filter((_, i) => i !== userReactionIndex);
        } else {
          // Add reaction
          newReactions = [...existingReactions, { user_id: user?.user_id, user_name: user?.name, emoji }];
        }
        return { ...msg, reactions: newReactions };
      }
      return msg;
    }));

    try {
      const res = await api.post(`/messages/${messageId}/react`, {
        message_id: messageId,
        emoji
      });
      // Update with server response to get accurate user_names
      setMessages(prev => prev.map(msg => 
        msg.message_id === messageId ? { ...msg, reactions: res.data.reactions } : msg
      ));
    } catch (error) {
      // Revert on error - refetch messages
      fetchMessages();
      toast.error('Failed to add reaction');
    }
  };

  const createTaskFromMessage = async (messageId) => {
    try {
      const response = await api.post(`/messages/${messageId}/create-task`);
      toast.success(`Task created: ${response.data.title}`);
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const deleteMessage = async (messageId) => {
    try {
      await api.delete(`/messages/${messageId}`);
      setMessages(prev => prev.filter(m => m.message_id !== messageId));
      toast.success('Message deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete message');
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??';
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-full">
          <Loader2 className="w-8 h-8 text-[#4A9CC7] animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="flex h-full overflow-hidden" data-testid="chat-container">
        {/* Conversation List - Full screen on mobile when no chat selected */}
        <div className={`
          ${mobileShowChat ? 'hidden md:flex' : 'flex'} 
          w-full md:w-72 border-r border-border bg-card flex-col flex-shrink-0
        `}>
          {/* Header */}
          <div className="p-4 border-b border-border">
            <h2 className="font-semibold text-foreground mb-3">{t('messages', 'Messages')}</h2>
            {/* Workspace Selector */}
            <Select 
              value={selectedWorkspace?.workspace_id || ''} 
              onValueChange={handleWorkspaceChange}
            >
              <SelectTrigger className="w-full h-10 text-sm" data-testid="chat-workspace-select">
                <SelectValue placeholder="Select workspace" />
              </SelectTrigger>
              <SelectContent>
                {workspaces.map(ws => (
                  <SelectItem key={ws.workspace_id} value={ws.workspace_id}>
                    <div className="flex items-center gap-2">
                      {ws.workspace_type === 'team' ? (
                        <Users className="w-3 h-3" />
                      ) : (
                        <User className="w-3 h-3" />
                      )}
                      {ws.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Conversation List */}
          <ScrollArea className="flex-1">
            <div className="p-2">
              {conversations.map((conv) => (
                <button
                  key={conv.conversation_id}
                  onClick={() => handleSelectConversation(conv)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                    activeConversation?.conversation_id === conv.conversation_id
                      ? 'bg-[#4A9CC7]/10 text-[#4A9CC7]'
                      : 'hover:bg-muted'
                  }`}
                  data-testid={`conversation-${conv.conversation_id}`}
                >
                  <div className={`relative w-10 h-10 md:w-8 md:h-8 rounded-lg flex items-center justify-center ${
                    conv.conversation_type === 'workspace' ? 'bg-blue-100 dark:bg-blue-900/30' :
                    conv.conversation_type === 'project' ? 'bg-purple-100 dark:bg-purple-900/30' :
                    'bg-muted'
                  }`}>
                    {conv.conversation_type === 'direct' ? (
                      <User className="w-5 h-5 md:w-4 md:h-4 text-muted-foreground" />
                    ) : (
                      <Hash className="w-5 h-5 md:w-4 md:h-4 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className={`font-medium text-sm truncate ${conv.unread_count > 0 ? 'font-semibold' : ''}`}>
                        {conv.name}
                      </p>
                      {conv.unread_count > 0 && (
                        <Badge 
                          className="bg-[#4A9CC7] text-white text-xs px-1.5 py-0.5 min-w-[20px] h-5 flex items-center justify-center ml-2"
                          data-testid={`unread-badge-${conv.conversation_id}`}
                        >
                          {conv.unread_count > 99 ? '99+' : conv.unread_count}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                      {conv.last_message ? (
                        <span className={conv.unread_count > 0 ? 'font-medium text-foreground' : ''}>
                          {conv.last_message.sender_name}: {conv.last_message.content}
                        </span>
                      ) : (
                        conv.conversation_type === 'workspace' ? 'Workspace chat' :
                        conv.conversation_type === 'project' ? 'Project chat' :
                        'Direct message'
                      )}
                    </p>
                  </div>
                </button>
              ))}
              
              {conversations.length === 0 && (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 text-muted-foreground/30" />
                  <p>No conversations yet</p>
                  <p className="text-xs mt-1">Select a workspace to see chats</p>
                </div>
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Chat Area - Full screen on mobile when chat selected */}
        <div className={`
          ${mobileShowChat ? 'flex' : 'hidden md:flex'} 
          flex-1 flex-col bg-background
        `}>
          {activeConversation ? (
            <>
              {/* Chat Header */}
              <div className="h-14 border-b border-border bg-card flex items-center px-3 md:px-4 gap-3">
                {/* Back button - Mobile only */}
                <button 
                  className="md:hidden p-2 -ml-2 hover:bg-muted rounded-lg"
                  onClick={handleBackToList}
                  data-testid="chat-back-btn"
                >
                  <ArrowLeft className="w-5 h-5 text-foreground" />
                </button>
                
                <div className="w-9 h-9 md:w-8 md:h-8 rounded-lg bg-[#4A9CC7]/10 flex items-center justify-center flex-shrink-0">
                  <Hash className="w-4 h-4 text-[#4A9CC7]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-sm text-foreground truncate">{activeConversation.name}</h3>
                  <p className="text-xs text-muted-foreground truncate">
                    {activeConversation.conversation_type} • {selectedWorkspace?.name}
                  </p>
                </div>
              </div>

              {/* Messages */}
              <ScrollArea className="flex-1 p-3 md:p-4">
                <div className="space-y-3 md:space-y-4">
                  {messages.length === 0 && (
                    <div className="text-center py-12 text-muted-foreground">
                      <MessageSquare className="w-12 h-12 mx-auto mb-3 text-muted-foreground/30" />
                      <p className="text-sm">No messages yet</p>
                      <p className="text-xs mt-1">Start the conversation!</p>
                    </div>
                  )}
                  {messages.map((message) => {
                    const isOwnMessage = message.sender_id === user?.user_id;
                    return (
                      <div 
                        key={message.message_id}
                        className={`flex gap-2 md:gap-3 group ${isOwnMessage ? 'flex-row-reverse' : ''}`}
                        data-testid={`message-${message.message_id}`}
                      >
                        {!isOwnMessage && (
                          <Avatar className="w-8 h-8 flex-shrink-0">
                            <AvatarImage src={message.sender_picture} />
                            <AvatarFallback className="bg-[#4A9CC7]/10 text-[#4A9CC7] text-xs">
                              {getInitials(message.sender_name)}
                            </AvatarFallback>
                          </Avatar>
                        )}
                        
                        <div className={`max-w-[80%] md:max-w-[70%] ${isOwnMessage ? 'items-end' : 'items-start'}`}>
                          {!isOwnMessage && (
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-medium text-foreground">{message.sender_name}</span>
                              <span className="text-[10px] text-muted-foreground">
                                {formatTime(message.created_at)}
                              </span>
                              {message.target_user_id && (
                                <span className="text-[10px] text-orange-500 flex items-center gap-0.5" data-testid={`targeted-badge-${message.message_id}`}>
                                  <Lock className="w-2.5 h-2.5" />To {message.target_user_name || 'you'}
                                </span>
                              )}
                            </div>
                          )}
                          
                          <div className={`rounded-2xl px-3 py-2 ${
                            isOwnMessage
                              ? message.target_user_id ? 'bg-orange-500 text-white rounded-br-md' : 'bg-[#4A9CC7] text-white rounded-br-md'
                              : message.target_user_id ? 'bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-800 rounded-bl-md' : 'bg-muted rounded-bl-md'
                          }`}>
                            <LinkifiedText 
                              text={message.content} 
                              className="text-sm whitespace-pre-wrap break-words"
                              linkClassName={isOwnMessage 
                                ? 'text-blue-100 hover:text-white underline cursor-pointer' 
                                : 'text-blue-500 hover:text-blue-700 underline cursor-pointer'
                              }
                              as="p"
                            />
                          </div>
                          
                          {isOwnMessage && (
                            <div className="flex justify-end mt-0.5 gap-2">
                              {message.target_user_id && (
                                <span className="text-[10px] text-orange-300 flex items-center gap-0.5">
                                  <Lock className="w-2.5 h-2.5" />To {message.target_user_name || 'member'}
                                </span>
                              )}
                              <span className="text-[10px] text-muted-foreground">
                                {formatTime(message.created_at)}
                              </span>
                            </div>
                          )}
                          
                          {/* Reactions */}
                          {message.reactions?.length > 0 && (
                            <div className="flex gap-1 mt-1 flex-wrap">
                              {Object.entries(
                                message.reactions.reduce((acc, r) => {
                                  if (!acc[r.emoji]) acc[r.emoji] = [];
                                  acc[r.emoji].push(r.user_name || r.user_id);
                                  return acc;
                                }, {})
                              ).map(([emoji, names]) => (
                                <span 
                                  key={emoji}
                                  className={`text-xs px-2 py-0.5 rounded-full cursor-pointer transition-colors ${
                                    names.some(n => n === user?.name || n === user?.user_id) 
                                      ? 'bg-[#4A9CC7]/20 text-[#4A9CC7] border border-[#4A9CC7]/30' 
                                      : 'bg-muted hover:bg-muted/80'
                                  }`}
                                  onClick={() => handleReaction(message.message_id, emoji)}
                                  title={names.join(', ')}
                                  data-testid={`reaction-${emoji}-${message.message_id}`}
                                >
                                  {emoji} {names.length}
                                </span>
                              ))}
                            </div>
                          )}
                          
                          {/* Message Actions - Inline horizontal */}
                          <div className={`flex opacity-0 group-hover:opacity-100 items-center gap-0.5 mt-1 transition-opacity ${isOwnMessage ? 'justify-end' : ''}`}>
                            {/* Emoji React */}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 rounded-full hover:bg-muted" title="React" data-testid={`react-msg-${message.message_id}`}>
                                  <Smile className="w-3.5 h-3.5 text-muted-foreground" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent>
                                <div className="flex gap-1 p-2">
                                  {EMOJIS.map((emoji) => (
                                    <button
                                      key={emoji}
                                      onClick={() => handleReaction(message.message_id, emoji)}
                                      className="hover:bg-muted p-1 rounded text-lg"
                                    >
                                      {emoji}
                                    </button>
                                  ))}
                                </div>
                              </DropdownMenuContent>
                            </DropdownMenu>

                            {/* Create Task */}
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-6 w-6 p-0 rounded-full hover:bg-muted"
                              onClick={() => createTaskFromMessage(message.message_id)}
                              title="Create task"
                              data-testid={`create-task-msg-${message.message_id}`}
                            >
                              <CheckSquare className="w-3.5 h-3.5 text-muted-foreground" />
                            </Button>

                            {/* Delete - own messages or admin/superadmin */}
                            {(isOwnMessage || user?.role === 'admin' || user?.role === 'superadmin') && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-6 w-6 p-0 rounded-full hover:bg-red-100 dark:hover:bg-red-500/15"
                                onClick={() => deleteMessage(message.message_id)}
                                title="Delete message"
                                data-testid={`delete-msg-${message.message_id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5 text-red-400 hover:text-red-500" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              {/* Message Input */}
              <div className="p-3 md:p-4 pb-6 md:pb-4 border-t border-border bg-card space-y-2">
                {/* Target selector */}
                <div className="flex items-center gap-2">
                  <Select value={targetUserId} onValueChange={setTargetUserId}>
                    <SelectTrigger className="h-7 w-auto min-w-[140px] max-w-[200px] text-xs" data-testid="chat-target-select">
                      <div className="flex items-center gap-1">
                        {targetUserId ? <Lock className="w-3 h-3 text-orange-500" /> : <Globe className="w-3 h-3 text-[#4A9CC7]" />}
                        <SelectValue placeholder="Everyone" />
                      </div>
                    </SelectTrigger>
                    <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                      <SelectItem value="everyone">
                        <div className="flex items-center gap-1.5"><Globe className="w-3 h-3 text-[#4A9CC7]" />Everyone</div>
                      </SelectItem>
                      {chatMembers.map(m => (
                        <SelectItem key={m.user_id} value={m.user_id}>
                          <div className="flex items-center gap-1.5"><User className="w-3 h-3" />{m.name || m.email}</div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {targetUserId && targetUserId !== 'everyone' && (
                    <span className="text-[10px] text-orange-500 flex items-center gap-1"><Lock className="w-3 h-3" />Only visible to you and recipient</span>
                  )}
                </div>
                <div className="flex gap-2 items-end">
                  <Input
                    ref={inputRef}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder={targetUserId && targetUserId !== 'everyone' ? `Message to ${chatMembers.find(m => m.user_id === targetUserId)?.name || 'member'}...` : t('type_message', 'Type a message...')}
                    onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                    disabled={sending}
                    className="flex-1 min-h-[44px] text-base md:text-sm"
                    data-testid="message-input"
                  />
                  <Button 
                    onClick={sendMessage}
                    disabled={sending || !newMessage.trim()}
                    className="bg-[#4A9CC7] hover:bg-[#3d8ab5] h-11 w-11 md:h-10 md:w-10 p-0 flex-shrink-0"
                    data-testid="send-message-btn"
                  >
                    {sending ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              <div className="text-center p-4">
                <MessageSquare className="w-16 h-16 mx-auto mb-4 text-muted-foreground/30" />
                <p className="font-medium">Select a conversation</p>
                <p className="text-sm mt-1">Choose a chat from the list to start messaging</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default Chat;
